import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ReviewDialog from "@/components/ReviewDialog";
import StudentInbox from "@/components/StudentInbox";
import CompleteProfileNudge from "@/components/CompleteProfileNudge";
import RecommendedHostels from "@/components/RecommendedHostels";
import { ShieldCheck, ShieldAlert, ShieldQuestion, CalendarDays, IndianRupee, Star, AlertOctagon, CheckCircle2, MessageCircle, MapPin, ExternalLink, Phone, Search } from "lucide-react";
import { SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY, buildSupportWhatsAppLink } from "@/lib/support";

function buildWhatsAppLink(b) {
  const digits = String(b.owner_phone || "").replace(/\D/g, "");
  const withCc = digits.length === 10 ? "91" + digits : digits;
  const msg =
    `Hi ${b.owner_name || "Hostel Owner"}! I've booked a bed at ${b.hostel_name} via HostelNow.%0A%0A` +
    `Booking ref: #${(b.id || "").slice(0, 8)}%0A` +
    (b.verification_code ? `Verification code: ${b.verification_code}%0A` : "") +
    `Check-in: ${b.check_in} → Check-out: ${b.check_out}%0A` +
    `Beds: ${b.beds} · Nights: ${b.nights}%0A` +
    (b.room_type ? `Room: ${b.room_type}%0A` : "") +
    `Amount paid: ₹${b.amount}%0A%0APlease confirm my check-in. Thanks!`;
  return `https://wa.me/${withCc}?text=${msg}`;
}

function buildMapsLink(b) {
  if (b.hostel_lat && b.hostel_lng) {
    return `https://www.google.com/maps/search/?api=1&query=${b.hostel_lat},${b.hostel_lng}`;
  }
  const q = encodeURIComponent(`${b.hostel_address || ""}, ${b.hostel_city || ""}`.trim());
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

const KYC_BADGE = {
  not_submitted: { label: "Not submitted", icon: ShieldAlert, cls: "bg-amber-100 text-amber-800" },
  pending: { label: "Under review", icon: ShieldQuestion, cls: "bg-amber-100 text-amber-700" },
  verified: { label: "Verified", icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", icon: ShieldAlert, cls: "bg-rose-100 text-rose-700" },
};

export default function StudentDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [reviewBooking, setReviewBooking] = useState(null);
  const load = () => api.get("/bookings/my").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const kyc = KYC_BADGE[user?.kyc_status] || KYC_BADGE.not_submitted;
  const KycIcon = kyc.icon;

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <CompleteProfileNudge />
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Student Dashboard</div>
            <h1 data-testid="student-welcome-heading" className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">Welcome, {user?.name?.split(" ")[0]} 👋</h1>
          </div>
          <div className={`px-4 py-2 rounded-full flex items-center gap-2 text-sm font-semibold ${kyc.cls}`}>
            <KycIcon size={16} /> KYC · {kyc.label}
          </div>
        </div>

        {(!user?.kyc_status || user?.kyc_status === "not_submitted") && (
          <div data-testid="kyc-cta-banner" className="mt-6 rounded-2xl bg-gradient-to-br from-orange-700 to-orange-900 text-white p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/15 grid place-items-center flex-shrink-0"><ShieldCheck size={24} /></div>
            <div className="flex-1">
              <div className="font-display font-black text-2xl">Complete Your KYC to Start Booking</div>
              <p className="text-sm text-orange-100 mt-1">Verify your Aadhaar once (takes ~2 minutes) and you're ready to book any hostel instantly.</p>
            </div>
            <Link to="/kyc" data-testid="kyc-cta-btn" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white text-orange-900 font-bold text-sm hover:bg-orange-50 flex-shrink-0">
              <ShieldCheck size={16} /> Verify now
            </Link>
          </div>
        )}

        {user?.kyc_status === "rejected" && user?.kyc_rejection_reason && (
          <div data-testid="kyc-rejected-banner" className="mt-6 rounded-2xl bg-rose-50 border-2 border-rose-300 p-5 flex items-start gap-3">
            <ShieldAlert className="text-rose-600 flex-shrink-0 mt-0.5" size={22} />
            <div className="flex-1">
              <div className="font-bold text-rose-900">Your KYC was rejected</div>
              <p className="text-sm text-rose-900 mt-1">Reason: <span className="font-semibold">{user.kyc_rejection_reason}</span></p>
              <Link to="/kyc" className="inline-block mt-2 text-xs font-bold text-rose-800 underline">Re-submit KYC →</Link>
            </div>
          </div>
        )}

        {user?.kyc_status === "pending" && (
          <div data-testid="kyc-pending-banner" className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3">
            <ShieldQuestion className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
            <div>
              <div className="font-bold text-amber-900">KYC under review</div>
              <p className="text-sm text-amber-900 mt-1">Our team is verifying your documents (usually within 24 hours). You can book while waiting, but confirmation happens after approval.</p>
            </div>
          </div>
        )}

        {bookings.filter((b) => b.payment_status !== "paid").length > 0 && (
          <div data-testid="unpaid-summary-banner" className="mt-6 rounded-2xl border-2 border-dashed border-rose-400 bg-rose-50 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-rose-600 text-white grid place-items-center flex-shrink-0"><AlertOctagon size={22} /></div>
            <div className="flex-1">
              <div className="font-black uppercase tracking-widest text-rose-800 text-xs">Action needed</div>
              <div className="mt-1 font-display font-bold text-xl text-rose-900">
                {bookings.filter((b) => b.payment_status !== "paid").length} booking{bookings.filter((b) => b.payment_status !== "paid").length > 1 ? "s" : ""} awaiting payment
              </div>
              <p className="text-sm text-rose-900 mt-1">
                These beds are <strong>not confirmed yet</strong> — money never moved. Scroll down to complete payment, or discard if you no longer need them.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  data-testid="unpaid-scroll-cta"
                  href="#my-bookings"
                  className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
                >
                  <IndianRupee size={14} /> Complete payment
                </a>
                <a
                  href={`tel:${SUPPORT_PHONE}`}
                  className="inline-flex items-center gap-1 h-9 px-4 rounded-full border-2 border-rose-400 text-rose-800 text-sm font-semibold hover:bg-rose-100"
                >
                  <Phone size={14} /> Need help? Call {SUPPORT_PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10" data-testid="quick-actions-section">
          <h2 className="font-display font-black text-3xl tracking-tighter">Quick actions</h2>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/hostels" data-testid="quick-action-find-hostel" className="card-lift bg-white border border-slate-200 rounded-2xl p-6 hover:border-orange-600">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 grid place-items-center"><Search size={18} /></div>
              <h3 className="mt-4 font-display font-bold text-xl">Find a Hostel</h3>
              <p className="text-sm text-slate-600 mt-1">Browse verified hostels near every coaching center.</p>
            </Link>
            <a href="#my-bookings" data-testid="quick-action-my-bookings" className="card-lift bg-white border border-slate-200 rounded-2xl p-6 hover:border-orange-600">
              <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-800 grid place-items-center"><CalendarDays size={18} /></div>
              <h3 className="mt-4 font-display font-bold text-xl">View My Bookings</h3>
              <p className="text-sm text-slate-600 mt-1">{bookings.length} booking{bookings.length === 1 ? "" : "s"} · jump to details below.</p>
            </a>
            <Link to="/kyc" data-testid="quick-action-kyc" className="card-lift bg-white border border-slate-200 rounded-2xl p-6 hover:border-orange-600">
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-800 grid place-items-center"><ShieldCheck size={18} /></div>
              <h3 className="mt-4 font-display font-bold text-xl">Complete KYC</h3>
              <p className="text-sm text-slate-600 mt-1">Upload Aadhaar (yours + guardian). Required at check-in.</p>
            </Link>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><IndianRupee size={18} /></div>
              <h3 className="mt-4 font-display font-bold text-xl">{bookings.filter(b => b.payment_status === "paid").length} paid bookings</h3>
              <p className="text-sm text-slate-600 mt-1">Total spent ₹{bookings.filter(b => b.payment_status === "paid").reduce((s, b) => s + b.amount, 0).toLocaleString("en-IN")}</p>
            </div>
          </div>
        </div>

        <RecommendedHostels />

        {/* Help & Support — always visible so students know where to get help */}
        <div data-testid="support-card" className="mt-10 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="w-12 h-12 rounded-full bg-white/15 grid place-items-center flex-shrink-0"><Phone size={22} /></div>
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-emerald-100">Help &amp; Support</div>
            <div className="font-display font-black text-2xl mt-0.5">Need help? We're one call away.</div>
            <div className="text-sm text-emerald-50 mt-1">Payment stuck · booking issues · check-in problems — call or WhatsApp us.</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <a data-testid="support-call-btn" href={`tel:${SUPPORT_PHONE}`} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white text-emerald-800 text-sm font-bold hover:bg-emerald-50">
              <Phone size={16} /> {SUPPORT_PHONE_DISPLAY}
            </a>
            <a data-testid="support-whatsapp-btn" href={buildSupportWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-bold border border-white/30">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>

        <div className="mt-12" id="my-bookings">
          <h2 className="font-display font-black text-3xl tracking-tighter">Your bookings</h2>
          <div className="mt-6 space-y-4">
            {bookings.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-16 text-center text-slate-500">
                No bookings yet. <Link to="/hostels" className="text-orange-800 font-semibold ml-1">Browse hostels →</Link>
              </div>
            )}
            {bookings.map((b) => {
              const today = new Date().toISOString().slice(0, 10);
              const isPaid = b.payment_status === "paid";
              const canReview = isPaid && !b.review_id && b.check_out <= today;
              return (
              <div
                key={b.id}
                data-testid={`my-booking-${b.id}`}
                data-payment-status={b.payment_status}
                className={`relative rounded-2xl overflow-hidden ${isPaid ? "bg-white border border-slate-200 p-5" : "bg-white border-2 border-dashed border-rose-300 p-5"}`}
              >
                {!isPaid && (
                  <>
                    {/* Diagonal warning stripe — impossible to miss in a screenshot */}
                    <div className="absolute -top-3 -right-16 rotate-45 bg-rose-600 text-white text-[10px] font-black tracking-widest uppercase px-16 py-1 shadow-md">
                      NOT PAID
                    </div>
                    <div data-testid="unpaid-warning" className="mb-4 rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900 flex items-start gap-2">
                      <AlertOctagon size={16} className="flex-shrink-0 mt-0.5 text-rose-600" />
                      <div>
                        <div className="font-bold uppercase tracking-wide">Payment pending — booking NOT confirmed</div>
                        <p className="mt-0.5">The hostel owner will NOT accept this as proof. Complete the Razorpay payment to receive a confirmed booking with a verification code.</p>
                      </div>
                    </div>
                  </>
                )}
                {isPaid && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-black tracking-widest uppercase px-2 py-1 rounded-full">
                    <CheckCircle2 size={11} /> PAID
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-5 items-center">
                  <img src={b.hostel_image} className="w-full sm:w-40 h-32 sm:h-24 object-cover rounded-xl" alt="" />
                  <div className="flex-1">
                    <div className="font-display font-bold text-lg">{b.hostel_name}</div>
                    <div className="text-sm text-slate-500">{b.hostel_city}</div>
                    <div className="text-sm mt-1">{b.check_in} → {b.check_out} · {b.nights} nights · {b.beds} bed(s)</div>
                    {isPaid && b.payment_ref && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-2 py-0.5">
                        Ref: {b.payment_ref}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {isPaid ? (
                      <>
                        <div className="font-display font-black text-2xl text-emerald-700">₹{b.amount.toLocaleString("en-IN")}</div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 mt-1">Paid via Razorpay</div>
                      </>
                    ) : (
                      <>
                        <div className="font-display font-black text-2xl text-rose-700">₹{b.amount.toLocaleString("en-IN")}</div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600 mt-1">Amount due</div>
                      </>
                    )}
                  </div>
                  {!isPaid && (
                    <Link to={`/booking/${b.id}`}>
                      <Button className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-semibold gap-1">
                        <IndianRupee size={14} /> Pay ₹{b.amount.toLocaleString("en-IN")}
                      </Button>
                    </Link>
                  )}
                  {canReview && (
                    <Button data-testid={`review-btn-${b.id}`} onClick={() => setReviewBooking(b)}
                      className="rounded-full bg-orange-800 hover:bg-orange-800 text-white gap-2">
                      <Star size={16} /> Review stay
                    </Button>
                  )}
                  {b.review_id && (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1"><Star size={12} className="fill-emerald-700" /> Reviewed</Badge>
                  )}
                </div>
                {isPaid && (b.owner_phone || b.hostel_address) && (
                  <div className="mt-4 grid sm:grid-cols-2 gap-3 pt-4 border-t border-dashed border-slate-200">
                    {b.owner_phone && (
                      <a
                        data-testid={`whatsapp-owner-${b.id}`}
                        href={buildWhatsAppLink(b)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors p-3"
                      >
                        <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center flex-shrink-0"><MessageCircle size={18} /></div>
                        <div className="min-w-0">
                          <div className="text-[10px] tracking-widest uppercase font-bold text-emerald-700">Message owner</div>
                          <div className="text-sm font-semibold text-emerald-900 truncate">{b.owner_name || "Hostel owner"} on WhatsApp</div>
                        </div>
                        <ExternalLink size={14} className="text-emerald-700 ml-auto flex-shrink-0" />
                      </a>
                    )}
                    {(b.hostel_address || (b.hostel_lat && b.hostel_lng)) && (
                      <a
                        data-testid={`open-maps-${b.id}`}
                        href={buildMapsLink(b)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors p-3"
                      >
                        <div className="w-9 h-9 rounded-full bg-orange-800 text-white grid place-items-center flex-shrink-0"><MapPin size={18} /></div>
                        <div className="min-w-0">
                          <div className="text-[10px] tracking-widest uppercase font-bold text-orange-800">Open location</div>
                          <div className="text-sm font-semibold text-orange-900 truncate">{b.hostel_address || b.hostel_city}</div>
                        </div>
                        <ExternalLink size={14} className="text-orange-800 ml-auto flex-shrink-0" />
                      </a>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
        <StudentInbox />
      </div>
      {reviewBooking && (
        <ReviewDialog
          booking={reviewBooking}
          open={!!reviewBooking}
          onClose={() => setReviewBooking(null)}
          onSaved={load}
        />
      )}
      <Footer />
    </div>
  );
}
